import { normalizeAuthorizationEndpoint } from './SGWTConnectAuthorizationUri';
import { generateRedirectUri } from './SGWTConnectRedirectUri';

/**
 * User-friendly SGWTConnect configuration with optional fields.
 */
export interface ISGWTConnectConfiguration {
  /**
   * Authorization endpoint is used to interact with the resource owner
   * and obtain an authorization grant.
   *
   * See https://tools.ietf.org/html/rfc6749#section-3.1
   */
  authorization_endpoint: string;

  /**
   * Client identifier is a unique string representing the registration
   * information provided by the client.
   *
   * See https://tools.ietf.org/html/rfc6749#section-2.2
   */
  client_id: string;

  /**
   * Authorization server will redirect the user-agent to `redirect_uri`
   * after completing its interaction with the resource owner.
   *
   * See https://tools.ietf.org/html/rfc6749#section-3.1.2
   */
  redirect_uri?: string;

  /**
   * The `response_type` parameter is used to inform the authorization
   * endpoint of the desired grant type. Defaults to `"id_token token"`,
   * but can be `"none"` or a combination of the following:
   *  - `"token"` indicates usage of the implicit grant type.
   *  - `"id_token"` requests an id_token from the auth server.
   *
   * See https://tools.ietf.org/html/rfc6749#section-3.1.1
   * See http://openid.net/specs/oauth-v2-multiple-response-types-1_0.html#Combinations
   */
  response_type?: string;

  /**
   * Access Token Scope specifies the scope of the access request in the
   * form of a case-sensitive string of unordered space-delimited values,
   * for example `"openid profile access_to_my_data"`.
   *
   * See https://tools.ietf.org/html/rfc6749#section-3.3
   */
  scope: string;

  /**
   * Authentication Context Class Reference values.
   *
   * See https://openid.net/specs/openid-connect-core-1_0.html#AuthRequest
   */
  acr_values?: string;

  /**
   * URL to which the the End-User's User Agent will be
   * redirected after a logout has been performed.
   *
   * See https://openid.net/specs/openid-connect-session-1_0.html#RPLogout
   */
  post_logout_redirect_uri?: string;

  /**
   * Adds the nonce string value used to associate a Client session with an ID Token,
   * and to mitigate replay attacks.
   * Default to `true`.
   */
  with_nonce?: boolean;

  /**
   * Overrides the key used by sgwtConnect for its storage. By default, this key is generated
   * with the hash of the configuration. In specific cases, the configuration can be dynamic
   * (for example the `acr_values` may differ depending on some conditions), and thus the storage
   * key can change, leading to unexpected behaviors.
   * Forcing a storage key helps to handle such situations.
   */
  storage_key?: string;
}

/**
 * Internal SGWTConnect configuration without optional fields.
 */
export interface ISGWTConnectConfigurationStrict extends ISGWTConnectConfiguration {
  /**
   * Authorization server will redirect the user-agent to `redirect_uri`
   * after completing its interaction with the resource owner.
   *
   * See https://tools.ietf.org/html/rfc6749#section-3.1.2
   */
  redirect_uri: string;

  /**
   * The `response_type` parameter is used to inform the authorization
   * endpoint of the desired grant type. Can be a combination of:
   *  - `"token"` indicates usage of the implicit grant type.
   *  - `"id_token"` requests an id_token from the auth server
   *
   * See https://tools.ietf.org/html/rfc6749#section-3.1.1
   * See http://openid.net/specs/oauth-v2-multiple-response-types-1_0.html#Combinations
   */
  response_type: string;

  /**
   * The custom `acr_values` parameter of SGConnect identifies the
   * authentication context.
   */
  acr_values: string;
}

/**
 * Normalizes the configuration by adding default fields,
 * and normalizing existing fields.
 * @param config Configuration object to normalize.
 */
export function normalizeConfiguration(
  config: ISGWTConnectConfiguration,
  location: Pick<Location, 'hash' | 'href' | 'protocol' | 'host'>,
): ISGWTConnectConfigurationStrict {
  return {
    ...config,
    response_type: config.response_type || 'id_token token',
    acr_values: config.acr_values || 'L2',

    // Normalize the authorization endpoint by stripping some
    // extraneous suffixes that users may add:
    authorization_endpoint: normalizeAuthorizationEndpoint(config.authorization_endpoint),

    // API consumers may:
    //  - specify a redirect_uri relative to origin
    //    in this case we prefix it with origin
    //  - not specify a redirect_uri at all
    //    in this case we define it for them, as it is mandatory
    redirect_uri: generateRedirectUri(location, config.redirect_uri),

    // API consumers may:
    //  - specify a post_logout_redirect_uri relative to origin
    //    in this case we prefix it with origin
    //  - not specify a redirect_uri at all
    //    in this case we don't do anything, as it is optional
    ...(config.post_logout_redirect_uri
      ? { post_logout_redirect_uri: generateRedirectUri(location, config.post_logout_redirect_uri) }
      : {}),
  };
}
